import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  ChevronLeft, Lock, Star, BookOpen, Heart, Users,
  MessageCircle, Flame, Crown, Moon, GraduationCap
} from 'lucide-react';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';
import * as api from '../lib/api';

const iconMap = {
  'message-circle': MessageCircle,
  'flame': Flame,
  'graduation-cap': GraduationCap,
  'book-open': BookOpen,
  'star': Star,
  'moon': Moon,
  'users': Users,
  'heart': Heart,
  'crown': Crown,
};

const categoryLabels = {
  activity: 'Activity',
  lms: 'Learning',
  spiritual: 'Spiritual',
  community: 'Community',
  ultimate: 'Ultimate',
};

const BadgesPage = ({ onBack, onNavigate }) => {
  const { profile } = useAuth();
  const [badges, setBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRamadanConfirm, setShowRamadanConfirm] = useState(false);
  const [claiming, setClaiming] = useState(false);

  const fetchBadges = useCallback(async () => {
    if (!profile?.id) return;
    try {
      const res = await api.getUserBadges(profile.id);
      setBadges(res.data || []);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [profile?.id]);

  useEffect(() => { fetchBadges(); }, [fetchBadges]);

  // Celebratory falling confetti — visible and festive
  useEffect(() => {
    const interval = setInterval(() => {
      confetti({
        particleCount: 8,
        spread: 160,
        startVelocity: 25,
        gravity: 0.6,
        ticks: 400,
        origin: { x: Math.random(), y: -0.05 },
        colors: ['#4AADA8', '#A99CC8', '#FFD700', '#E8917B', '#ACBA9D'],
        scalar: 1.1,
        disableForReducedMotion: true,
      });
    }, 400);
    return () => clearInterval(interval);
  }, []);

  const handleClaimRamadan = async () => {
    setClaiming(true);
    try {
      await api.claimRamadanBadge(profile.id);
      confetti({ particleCount: 150, spread: 100, origin: { y: 0.5 }, colors: ['#4AADA8', '#A99CC8', '#FFD700'] });
      await fetchBadges();
    } catch { /* ignore */ }
    setClaiming(false);
    setShowRamadanConfirm(false);
  };

  // The "Learning" (lms) category is intentionally hidden from the Badges page —
  // these badges no longer count toward the Marriage Readiness score. Data and
  // earning logic remain intact; only the UI grouping below excludes them.
  const HIDDEN_CATEGORIES = ['lms'];
  const visibleBadges = badges.filter(b => !HIDDEN_CATEGORIES.includes(b.category || 'other'));

  const grouped = {};
  visibleBadges.forEach(b => {
    const cat = b.category || 'other';
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(b);
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="max-w-[1000px] mx-auto p-4 lg:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 w-48 bg-primary/10 rounded" />
          <div className="grid grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <div key={i} className="h-40 bg-primary/5 rounded-xl" />)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1000px] mx-auto p-4 lg:p-8 pb-24" data-testid="badges-page">
      {/* Ramadan Confirmation Modal */}
      {showRamadanConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-sm w-full mx-4 shadow-2xl" data-testid="ramadan-confirm-modal">
            <h3 className="font-heading text-lg font-semibold text-text-main mb-3">Claim Ramadan Badge</h3>
            <p className="text-sm text-text-muted mb-6">
              I confirm I have completed a full Ramadan fast including making up any missed fasts.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowRamadanConfirm(false)} className="px-4 py-2 text-sm text-text-muted hover:bg-gray-100 rounded-lg"
                data-testid="ramadan-cancel">Cancel</button>
              <button onClick={handleClaimRamadan} disabled={claiming}
                className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50"
                data-testid="ramadan-confirm">
                {claiming ? 'Claiming...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={onBack} className="p-2 hover:bg-primary/10 rounded-full" data-testid="badges-back-btn">
          <ChevronLeft className="w-6 h-6 text-primary" />
        </button>
        <div>
          <h1 className="font-heading text-2xl font-semibold text-primary">Badges</h1>
          <p className="text-sm text-text-muted">
            {visibleBadges.filter(b => b.earned).length} of {visibleBadges.length} earned
          </p>
        </div>
      </div>

      {/* Shahadah Certificate entry point */}
      <button
        onClick={() => onNavigate?.('shahadah-certificate')}
        className="w-full flex items-center gap-4 p-5 mb-8 rounded-arch-lg text-left transition-all hover:shadow-md"
        style={{ background: 'linear-gradient(90deg, rgba(74,173,168,0.10), rgba(169,156,200,0.10))', border: '1px solid rgba(74,173,168,0.2)' }}
        data-testid="shahadah-certificate-link"
      >
        <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{ background: 'rgba(74,173,168,0.15)' }}>
          <Moon className="w-6 h-6 text-primary" aria-hidden="true" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-medium text-text-main">Generate your Shahadah Certificate</p>
          <p className="text-sm text-text-muted">Create a personalised keepsake commemorating your declaration of faith</p>
        </div>
        <ChevronLeft className="w-5 h-5 text-text-muted rotate-180 shrink-0" aria-hidden="true" />
      </button>

      {/* Badge Grid by Category */}
      {Object.entries(grouped).map(([category, catBadges]) => (
        <div key={category} className="mb-8">
          <h2 className="text-sm font-medium text-text-main mb-4 flex items-center gap-2">
            <Star className="w-4 h-4 text-primary" />
            {categoryLabels[category] || category}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {catBadges.map(badge => {
              const Icon = iconMap[badge.icon] || Star;
              const isRamadan = badge.name === 'Ramadan Badge';
              return (
                <motion.div
                  key={badge.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`relative glass-card rounded-xl p-5 text-center transition-all overflow-hidden ${
                    badge.earned ? 'border border-primary/20' : 'opacity-70'
                  }`}
                  data-testid={`badge-card-${badge.name.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {/* Gold Hero ribbon — top-right corner, only on Community Organizer with 10+ referrals */}
                  {badge.hero_ribbon && (
                    <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden pointer-events-none">
                      <div className="absolute top-[14px] right-[-18px] w-[72px] rotate-45 bg-yellow-400 text-[7px] font-bold text-yellow-900 text-center py-0.5 shadow-sm tracking-wide">
                        HERO
                      </div>
                    </div>
                  )}
                  <div className={`w-16 h-16 rounded-full mx-auto mb-3 flex items-center justify-center ${
                    badge.earned
                      ? 'bg-primary/15 border-2 border-primary/30'
                      : 'bg-primary/5 border-2 border-dashed border-primary/15'
                  }`}>
                    {badge.earned ? (
                      <Icon className="w-7 h-7 text-primary" />
                    ) : (
                      <Lock className="w-5 h-5 text-primary/30" />
                    )}
                  </div>
                  <p className={`text-sm font-medium mb-1 ${badge.earned ? 'text-text-main' : 'text-text-muted'}`}>
                    {badge.name}
                  </p>
                  <p className="text-[10px] text-text-muted leading-relaxed">{badge.description}</p>
                  {badge.earned && badge.earned_at && (
                    <p className="text-[10px] text-primary mt-2 font-medium">
                      Earned {formatDate(badge.earned_at)}
                    </p>
                  )}
                  {badge.is_placeholder && !badge.earned && (
                    <p className="text-[9px] text-secondary mt-2 font-medium">Coming Soon</p>
                  )}
                  {/* Ramadan self-report button */}
                  {isRamadan && !badge.earned && !badge.is_placeholder && (
                    <button onClick={() => setShowRamadanConfirm(true)}
                      className="mt-3 px-3 py-1.5 bg-primary text-white rounded-full text-[11px] font-medium hover:bg-primary/90 transition-colors"
                      data-testid="claim-ramadan-btn">
                      Claim Ramadan Badge
                    </button>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default BadgesPage;
